import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as textFieldEdit from 'text-field-edit';

import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { FusePreviewDialogComponent } from '@fuse/components/preview-dialog/preview-dialog.component';
import { bytesToSize, INewComment } from '@appshared';
import { IComment } from '@appshared';
import { PreviewModel } from '@fuse/components/preview-dialog/preview-dialog.model';

import { locale as vn } from '../i18n/vn';
import { locale as english } from '../i18n/en';

@Component({
  selector: 'app-simple-comment',
  templateUrl: './simple-comment.component.html',
  styleUrls: ['./simple-comment.component.scss'],
})
export class SimpleCommentComponent implements OnInit, OnDestroy {
  bytesToSize = bytesToSize;

  private _previewDialogRef: MatDialogRef<any>;

  formGroup: FormGroup = new FormGroup({
    commentText: new FormControl(),
  });

  _unsubscribeAll = new Subject();

  showEmojiPicker = false;

  selectedFiles: Set<File> = new Set<File>();

  // whether user is focusing on the text-area or not
  focusingTextareaEle = false;

  // is previewing attachment file
  isPreviewingFile = false;

  // ready to render the emoji selection box or not, otherwise it will slow down the whole renderring process
  readyToRender = false;

  @Input() comments: IComment[];

  private _isDisable: boolean;
  @Input()
  set disable(isDisable: boolean) {
    this._isDisable = isDisable;

    if (isDisable) {
      this.formGroup.disable();
    } else {
      this.formGroup.enable();
    }
  }
  get disable(): boolean {
    return this._isDisable;
  }

  @Output() newCommentEvent = new EventEmitter<INewComment>();

  @ViewChild('fileInput') fileInput: ElementRef;

  @ViewChild('textarea', { static: false }) textarea: ElementRef;
  @ViewChild('commentBox') commentBox: ElementRef;
  @ViewChild('emojiBox') emojiBox: ElementRef;

  constructor(private _fuseTranslationLoaderService: FuseTranslationLoaderService, private _matDialog: MatDialog) {
    this._fuseTranslationLoaderService.loadTranslations(english, vn);
  }

  ngOnInit(): void {}

  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  onFileSelected(event): void {
    const fileList: FileList = event.target.files;
    let selected = false;
    this.selectedFiles.forEach(file => {
      if (fileList.item(0).name === file.name) {
        selected = true;
      }
    });

    if (!selected) {
      this.selectedFiles.add(fileList.item(0));
    }

    this.fileInput.nativeElement.value = null;
  }

  removeAttachment(attachment: File): void {
    this.selectedFiles.delete(attachment);
  }

  toggleEmojiPicker(): void {
    // start to render the Emoji Picker
    this.readyToRender = true;
    this.showEmojiPicker = !this.showEmojiPicker;
  }

  addEmoji(event): void {
    // use textFieldEdit to support undo feature
    textFieldEdit.insert(this.textarea.nativeElement, event.emoji.native);
  }

  // this route will allow to remove the current attachment
  openPreviewBeforeSend(file: File): void {
    const data: PreviewModel = {
      preview: {
        file: file,
        fileSize: file.size,
        fileName: file.name,
      },
    };
    this.openPreviewDialog(data)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(result => {
        // if true, remove this file
        if (result) {
          this.selectedFiles.delete(file);
        }
      });
  }

  openPreviewAfterSend(commentData: IComment, attachIdx: number): void {
    const attachData = commentData.attachments[attachIdx];
    const documentId = commentData.attachmentIds[attachIdx];
    const data: PreviewModel = {
      review: {
        fileName: attachData.name,
        downloadUrl: `sale-data-svc${attachData.url}${documentId}?type=DOWNLOAD`,
        displayUrl: `sale-data-svc${attachData.url}${documentId}?type=DISPLAY`,
        author: {
          userName: commentData.author.userName,
          avatar: commentData.avatar,
        },
        attachmentTime: commentData.createdDate,
      },
    };

    this.openPreviewDialog(data);
  }

  private openPreviewDialog(data: PreviewModel): Observable<any> {
    this.isPreviewingFile = true;
    this._previewDialogRef = this._matDialog.open(FusePreviewDialogComponent, {
      disableClose: false,
    });
    this._previewDialogRef.componentInstance.data = data;
    const observe = this._previewDialogRef.afterClosed();

    observe.pipe(takeUntil(this._unsubscribeAll)).subscribe(result => {
      // notify that previewing is already finished
      this.isPreviewingFile = false;
    });

    return observe;
  }

  sendMessage(): void {
    // don't send an empty one (without text AND no attachment)
    if (!this.textarea.nativeElement.value && this.selectedFiles.size === 0) {
      return;
    }

    this.newCommentEvent.emit({
      detail: this.textarea.nativeElement.value,
      attachments: Array.from(this.selectedFiles),

      // this chat box is simple, no parent & children relationship, so the parentCommentId will be empty
      parentCommentId: '',
    });

    // clear the current data
    this.resetData();

    // Notify the UI to stop focusing the text area and show the list of comments
    // To allow user see the immediately content of the new comment after getting response from BE
    this.focusingTextareaEle = false;
  }

  private _resetData(): void {
    this.textarea.nativeElement.value = '';
    this.selectedFiles.clear();
  }

  public resetData(): void {
    this._resetData();
  }

  @HostListener('window:click', ['$event'])
  handleKeyDown(event: Event): void {
    // For chat box:  if the click happen outside the comment box area, return the chat section to normal
    if (this.isOutsideElement(event, this.commentBox)) {
      if (
        // if is previewing file, ignore the check
        !this.isPreviewingFile &&
        // if the click is from fileInput element, which is currently put outside the commentBox, ignore also
        event.target !== this.fileInput.nativeElement
      ) {
        this.focusingTextareaEle = false;
      }
    }

    // For emoji box: if the click happen outside the emoji box area, close the emoji picker
    if (this.isOutsideElement(event, this.emojiBox)) {
      // if it is open, then close
      if (this.showEmojiPicker) {
        this.showEmojiPicker = false;
      }
    }
  }

  private isOutsideElement(event: Event, parentEle: ElementRef): boolean {
    return event.target !== parentEle.nativeElement && !parentEle.nativeElement.contains(event.target);
  }
}
